import { useCallback, useEffect, useRef } from "react";

/**
 * Debounces a search callback while still allowing the pending search to be
 * executed immediately (for example, when the user presses Enter).
 */
const useDebouncedSearch = (onSearch, delay = 500) => {
  const callbackRef = useRef(onSearch);
  const timerRef = useRef(null);
  const latestArgsRef = useRef([]);

  useEffect(() => {
    callbackRef.current = onSearch;
  }, [onSearch]);

  const cancelSearch = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const debouncedSearch = useCallback(
    (...args) => {
      latestArgsRef.current = args;
      cancelSearch();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        callbackRef.current?.(...latestArgsRef.current);
      }, delay);
    },
    [cancelSearch, delay],
  );

  const searchImmediately = useCallback(
    (...args) => {
      if (args.length > 0) latestArgsRef.current = args;
      cancelSearch();
      return callbackRef.current?.(...latestArgsRef.current);
    },
    [cancelSearch],
  );

  useEffect(() => cancelSearch, [cancelSearch]);

  return { debouncedSearch, searchImmediately, cancelSearch };
};

export default useDebouncedSearch;
